import { Fragment } from 'react';
import { useSetRecoilState } from 'recoil';
import compact from 'lodash/compact';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isBoolean from 'lodash/isBoolean';
import classNames from 'classnames';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { FETCH_ORIGIN, Settings } from 'common/types';
import { SystemField } from 'common/graphqlTypes';
import { PARSE_TYPE, TOKEN_TYPE } from 'common/util/filter/types';
import { logToFetchState } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';

import FacetValue from 'components/common/FacetValue/FacetValue';

import './LogLine.scss';
import {
  CustomLogValue,
  FIELD_CATEGORIES,
  prepareDisplayedFieldsSelectors,
  StrictLogEntry,
  SystemLogValue,
} from './logLineUtils';

type LogLineProps = {
  continuationToken:  string;
  logEntry:           StrictLogEntry | null;
  filter:             string;
  index:              number;
  settings:           Settings;
  disableLineBreaks?: boolean;
  timezone:           string | null;
};

export function SeverityIcon({
  severityLevel = 1,
  isCellDisplay = true,
  classnames = '',
}: {
  severityLevel: number;
  isCellDisplay?: boolean;
  classnames?: string;
}): JSX.Element {
  const iconClassNames = classNames(
    'LogLine__Icon',
    {
      'LogLine__Icon--CellDisplay': isCellDisplay,
      'LogLine__Icon--info': severityLevel < 4,
      'LogLine__Icon--warn': severityLevel === 4,
      'LogLine__Icon--error': severityLevel === 5,
      'LogLine__Icon--danger': severityLevel > 5,
    },
    classnames,
  );
  return <div className={iconClassNames} data-test-id={ELEMENT.searchLogsSeverityIcon} />;
}

type MarkupFunction = (field: string, text: string, preserveLineBreaks?: boolean, isTruncated?: boolean) => JSX.Element;

const prepareSystemFieldElement = (
  logValue:   SystemLogValue,
  index:      number,
  fieldIndex: number,
): JSX.Element => {

  const testId = createTestId(ELEMENT.searchLogsLogLine, logValue.type, index, fieldIndex);
  return (
    <span
      className="LogLine__Field--System"
      data-token-type={TOKEN_TYPE.FIELD}
      data-test-id={testId}
      key={testId}
    >
      {logValue.value}{'\u200B'}
    </span>
  );
};

const prepareCustomFieldElement = (
  { type: field, value }: CustomLogValue,
  continuationToken: string,
  index:             number,
  markup:            MarkupFunction,
  prependSeparator = false,
  isTruncated      = false,
): JSX.Element => {
  const key = [continuationToken, String(index), field].join('-');
  const isStringValue = isString(value);
  const isNumberValue = isNumber(value);
  const content = markup(field, isStringValue ? `'${value}'` : String(value), false, isTruncated);
  const valueClassName = classNames('LogLine__Value', {
    'LogLine__Value--String': isStringValue,
    'LogLine__Value--Number': isNumberValue,
  });

  let valueTokenType = TOKEN_TYPE.UNKNOWN;
  if (isStringValue) {
    valueTokenType = TOKEN_TYPE.STRING;
  } else if (isNumberValue) {
    valueTokenType = TOKEN_TYPE.NUMBER;
  } else if (isBoolean(value)) {
    valueTokenType = TOKEN_TYPE.BOOLEAN;
  }

  return (
    <Fragment key={key}>
      {prependSeparator && <span className="LogLine__TokenSeparator">|</span>}
      <span
        className="LogLine__Token"
        data-token-type={PARSE_TYPE.TERM}
      >
        <span data-term-field={field}>{field}</span>
        <span className="LogLine__Equals">=</span>
        <span
          data-term-value-type={valueTokenType}
          className={valueClassName}
        >
          {content}
        </span>
      </span>
    </Fragment>
  );
};

const prepareMessageFieldElement = (
  logValue:          SystemLogValue,
  disableLineBreaks: boolean | undefined,
  markup:            MarkupFunction,
  index:             number,
  isTruncated      = false,
): JSX.Element => (
  <span
    className={classNames({
      'LogLine__Field--Message': disableLineBreaks,
      'LogLine__Field--WrappedMessage': !disableLineBreaks,
    })}
    data-token-type={TOKEN_TYPE.UNKNOWN}
    key={createTestId(K.FIELD.MESSAGE, index)}
  >
    {markup(K.FIELD.MESSAGE, logValue.value, true, isTruncated)}
  </span>
);

/**
 * This component represents a single log line.
 *
 * @prop continuationToken    the continuationToken of the log line, serving as id
 * @prop logEntry             the log entry to display
 * @prop filter               the filter text to be highlighted
 * @prop index                index of this line in the data
 * @prop settings             user settings (some control which fields are displayed)
 * @prop disableLineBreaks    if true, line breaks in logline content will be ignored (defaults to true)
 */
function LogLine({
  continuationToken,
  logEntry,
  filter,
  index,
  settings,
  disableLineBreaks = true,
  timezone,
}: LogLineProps): JSX.Element {

  const setLogToFetch = useSetRecoilState(logToFetchState);

  // Perform text transformations on the given bit of text. For example, highlight parts that match the filter.
  const markup = (
    field: string,
    value: string,
    preserveLineBreaks = false,
    isTruncated = false,
  ): JSX.Element => (
      <FacetValue
        field={field}
        value={value}
        filter={filter}
        settings={settings}
        doHighlight
        doLinkify
        disableLineBreaks={!preserveLineBreaks && disableLineBreaks}
        isTruncated={isTruncated}
        continuationToken={continuationToken}
      />
  );

  if (!continuationToken || !logEntry) {
    return <span>Loading...</span>;
  }

  let numTerms = 0;
  const allElements = compact(
    prepareDisplayedFieldsSelectors(logEntry, {...settings, timezone })
      .map(({ type, category, select }, fieldIndex) => {
        const logValue = select(logEntry, {...settings, timezone });

        if (!logValue) {
          return null;
        }

        const adjustedType = (type === SystemField.rawLogLine) ? K.FIELD.MESSAGE : type;
        const isTruncated = logEntry.facetsWithTruncatedValues.includes(adjustedType);
        if (type === SystemField.rawLogLine) {
          return prepareMessageFieldElement(logValue as SystemLogValue, disableLineBreaks, markup, index, isTruncated);
        }

        if (category === FIELD_CATEGORIES.SYSTEM) {
          return prepareSystemFieldElement(logValue as SystemLogValue, index, fieldIndex);
        }

        if (category === FIELD_CATEGORIES.CUSTOM) {
          numTerms += 1;
          return prepareCustomFieldElement(logValue, continuationToken, index, markup, numTerms > 1, isTruncated);
        }

        return null;
      }),
  );

  const noCustomOrSystemFieldsDisplayed = allElements.length === 0;
  const fieldContainerClass = classNames({ 'LogLine__FieldContainer--Wrapped': !disableLineBreaks });

  // tell user if attributes were elided
  const moreEl = !logEntry.facetsNumberLimitExceeded ? null : (
    <span>
      <span className="LogLine__TokenSeparator">|</span>
      <span className="LogLine__MoreAttributes">&hellip;</span>
      <span data-is-more-link="true" className="link" onClick={() => setLogToFetch({ continuationToken: continuationToken || '', origin: FETCH_ORIGIN.ATTRIBUTES })}>more attributes</span>
    </span>
  );


  return (
    <>
      <SeverityIcon severityLevel={logEntry.severity} isCellDisplay={!disableLineBreaks} />
      <div className={fieldContainerClass}>
        {noCustomOrSystemFieldsDisplayed && (
          <span className="LogLine__NoFields">
            No fields to display for this event (click Display button to change)
          </span>
        )}
        {allElements}
        {moreEl}
      </div>
    </>
  );
}

export default LogLine;
